import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import { AnalyticsStepTwoTemplate as template } from './analytics-step-two.template';
import { AnalyticsStepTwoStyles as styles } from './analytics-step-two.styles';
import { IBuyerAnalyticsService, IVppaService } from '../../../../services';
import { VppaRankings } from '../../../../types';
import { GRAPH_PERIOD_TIME, PRODUCT_TYPE } from '../../../../utils';
import { DrilldownExpectedSettlementGraph } from './drilldown-expected-settlement-graph/drilldown-expected-settlement-graph';
import { DrilldownFloatingPriceGraph } from './drilldown-floating-price-graph/drilldown-floating-price-graph';
import { DrilldownImpliedCarbonGraph } from './drilldown-implied-carbon-graph/drilldown-implied-carbon-graph';
import { DrilldownNodalLmeGraph } from './drilldown-nodal-lme-graph/drilldown-nodal-lme-graph';
import { logger, ZeroGridPro as zeroAgGrid } from '@genesislcap/foundation-zero-grid-pro';
import { IEnumService } from '../../../../services/enum.service';

const name = 'analytics-step-two';

@customElement({
  name,
  template,
  styles,
})
export class AnalyticsStepTwo extends FASTElement {
  @IBuyerAnalyticsService buyerAnalyticsService: IBuyerAnalyticsService;
  @IVppaService vppaService: IVppaService;
  @IEnumService enumService: IEnumService;
  @observable selectedVppaRankings: VppaRankings[] = [];
  @observable vppaDrilldownGrid: zeroAgGrid;
  @observable timePeriod: GRAPH_PERIOD_TIME = GRAPH_PERIOD_TIME.THREE_YEAR;
  @observable expectedSettlementGraph: DrilldownExpectedSettlementGraph;
  @observable floatingPriceGraph: DrilldownFloatingPriceGraph;
  @observable impliedCarbonGraph: DrilldownImpliedCarbonGraph;
  @observable nodalLmeGraph: DrilldownNodalLmeGraph;
  @observable forecastDataArray: Array<any> = [];
  @observable historicalDataArray: Array<any> = [];
  @observable projectNames: Array<String> = [];
  @observable disableButtons: boolean = false;
  @observable contractColors = {};
  @observable graphColors = [
    '#1ABC9C',
    '#8E44AD',
    '#C0392B',
    '#2980B9',
    '#F1C40F',
    '#27AE60',
    '#95A5A6',
    '#D35400',
    '#3498DB',
    '#BDC3C7',
    '#F39C12',
    '#16A085',
    '#ECF0F1',
    '#E74C3C',
    '#7F8C8D',
    '#9B59B6',
    '#2ECC71',
    '#34495E',
    '#E67E22',
  ];

  connectedCallback(): void {
    super.connectedCallback();
    this.selectedVppaRankings = this.buyerAnalyticsService.selectedVppas;
    this.associateContractToColor();
    this.setGridOptions();
    this.generateGraphs(this.selectedVppaRankings, this.timePeriod);
  }

  public associateContractToColor() {
    this.selectedVppaRankings.forEach((contract, index) => {
      if (!this.contractColors[contract.VPPA_ID]) {
        this.contractColors[contract.VPPA_ID] = this.graphColors[index];
      }
    });
    this.expectedSettlementGraph.colors = this.contractColors;
    this.floatingPriceGraph.colors = this.contractColors;
    this.impliedCarbonGraph.colors = this.contractColors;
    this.nodalLmeGraph.colors = this.contractColors;
  }

  public generateGraphs(selectedVppas, timePeriod) {
    this.getData(selectedVppas, timePeriod);
  }

  public timePeriodChanged(oldValue, newValue) {
    if (
      this.buyerAnalyticsService.stepTwoSelectedVppas &&
      this.buyerAnalyticsService.stepTwoSelectedVppas.length &&
      Number(oldValue) !== Number(newValue)
    ) {
      this.generateGraphs(this.buyerAnalyticsService.stepTwoSelectedVppas, newValue);
    }
  }

  private getData(vppaData: Array<VppaRankings>, timePeriod: number) {
    this.projectNames = [];
    this.forecastDataArray = [];
    this.historicalDataArray = [];
    if (vppaData && vppaData.length) {
      let startDate = new Date();
      const endTimestamp = new Date(
        startDate.setFullYear(startDate.getFullYear() + Number(timePeriod)),
      );
      startDate = new Date();
      const startDateHistorical = new Date(
        startDate.setFullYear(
          startDate.getFullYear() - (this.timePeriod === GRAPH_PERIOD_TIME.ONE_YEAR ? 1 : 3),
        ),
      );
      this.vppaService
        .getBuyerAnalyticsData(
          vppaData.map((x) => x.VPPA_ID),
          new Date(),
          endTimestamp,
          startDateHistorical,
        )
        .then(
          async (response) => {
            this.projectNames = response.REPLY.map((x) => x.PROJECT_NAME);
            this.forecastDataArray = response.REPLY.map((x) => x.FORECAST_DATA_LIST);
            this.historicalDataArray = response.REPLY.map((x) => x.HISTORICAL_DATA_LIST);

            if (
              (this.buyerAnalyticsService.currentPreferences?.PRODUCT_TYPE ?? '') ===
              PRODUCT_TYPE.RECS
            ) {
              this.expectedSettlementGraph.renderDisabledGraph();
              this.floatingPriceGraph.renderDisabledGraph();
            } else {
              this.expectedSettlementGraph.projectNames = [];
              this.floatingPriceGraph.projectNames = [];
              this.expectedSettlementGraph.renderGraph(this.forecastDataArray, this.projectNames);
              this.floatingPriceGraph.renderGraph(this.forecastDataArray, this.projectNames);
            }

            this.impliedCarbonGraph.projectNames = [];
            this.impliedCarbonGraph.renderGraph(this.forecastDataArray, this.projectNames);
            this.nodalLmeGraph.projectNames = [];
            this.nodalLmeGraph.renderGraph(this.historicalDataArray, this.projectNames);
          },
          (error) => {
            logger.error(error);
          },
        );
    } else {
      this.expectedSettlementGraph.disabledOptionsButton = true;
      this.floatingPriceGraph.disabledOptionsButton = true;
      this.impliedCarbonGraph.disabledOptionsButton = true;

      this.expectedSettlementGraph.renderDisabledGraph();
      this.floatingPriceGraph.renderDisabledGraph();
      this.impliedCarbonGraph.renderDisabledGraph();
    }
  }

  public setGridOptions() {
    this.vppaDrilldownGrid.gridOptions = {
      onGridReady: (grid) => {
        this.vppaDrilldownGrid.gridApi.setRowData(this.selectedVppaRankings);
        this.checkVppasSelected();

        if (
          (this.buyerAnalyticsService.currentPreferences?.PRODUCT_TYPE ?? '') ===
          PRODUCT_TYPE.VIRTUAL_PPA
        ) {
          this.vppaDrilldownGrid.columnApi.setColumnVisible('EXPECTED_SETTLEMENT', false);
        }

        grid.api.addEventListener('rowSelected', () => {
          this.checkVppasSelected();
        });
      },
      onCellClicked: (params) => {
        if (params.column['colId'] === 'ACTIONS') {
          this.vppaDrilldownGrid.gridApi.refreshCells({
            columns: [params.columnApi.getColumn('PROJECT_NAME')],
            force: true,
          });
        }
      },
      rowClass: 'transparent-background',
      rowSelection: 'multiple',
      rowMultiSelectWithClick: true,
      suppressRowClickSelection: true,
      tooltipShowDelay: 300,
      defaultColDef: {
        resizable: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
      },
    };
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
  }

  public checkVppasSelected() {
    this.buyerAnalyticsService.stepTwoSelectedVppas =
      this.vppaDrilldownGrid.gridApi.getSelectedRows();
    this.disableButtons = !this.buyerAnalyticsService.stepTwoSelectedVppas.length;
  }

  public returnToPreviousStep() {
    this.buyerAnalyticsService.currentStep = 1;
    this.selectedVppaRankings = [];
  }

  public proceedToSummary() {
    this.checkVppasSelected();
    if (this.buyerAnalyticsService.stepTwoSelectedVppas.length) {
      this.buyerAnalyticsService.currentStep = 3;
    }
  }
}
